// Merge CONSECUTIVE same-location route stops into grouped "stops" — the same
// way the day-plan timeline does (see `buildNewSteps` in RoutesChart.jsx) — so
// the route-detail page (map + Stops table) reads identically to the day-plan.
//
// The DB stores one route_step per order. When several orders share one physical
// stop (e.g. a cargo bike reloading at the depot between capacity batches, or
// several deliveries at the same building) the day-plan collapses the
// consecutive ones into a single numbered badge carrying every order. This
// helper reproduces that grouping for any `route.steps` array.
//
// Merge keys (must also be CONSECUTIVE and same status):
//   PICKUP   → pickup coords + pickupName (OVO-023 — phone is noisy: the
//              same depot arrives with a phone on some orders and an empty
//              one on others, fragmenting one physical visit)
//   DELIVERY → delivery coords + deliveryPhone + deliveryName
//   SERVICE  → never merges (single-destination by design)

const isSameStop = (a, b) => {
  if (!a?.order || !b?.order || a.type !== b.type) return false;
  if (a.status !== b.status) return false;
  if (a.type === 'PICKUP') {
    return (
      a.order.pickupAddressLat === b.order.pickupAddressLat &&
      a.order.pickupAddressLng === b.order.pickupAddressLng &&
      a.order.pickupName === b.order.pickupName
    );
  }
  if (a.type === 'DELIVERY') {
    return (
      a.order.deliveryAddressLat === b.order.deliveryAddressLat &&
      a.order.deliveryAddressLng === b.order.deliveryAddressLng &&
      a.order.deliveryPhone === b.order.deliveryPhone &&
      a.order.deliveryName === b.order.deliveryName
    );
  }
  return false;
};

// Returns grouped stops, each renumbered 1..N. A group keeps the FIRST step's
// fields (id, type, arrivalTime, order, …) and adds:
//   stopNumber  — 1-based position among the grouped stops (matches the day-plan)
//   orderIds[]  — every order at this physical stop
//   orders[]    — the full order objects (for codes / statuses)
//   mergedSteps[] — the raw steps it covers (so callers can derive per-stop
//                   actual times from the first arrival + last finish)
//   finishTime  — the LAST merged step's finish (the group's departure)
export function mergeRouteSteps(steps = []) {
  const src = (steps || []).filter((s) => s && s.type !== 'DRIVER');
  const groups = [];
  let i = 0;
  while (i < src.length) {
    const head = src[i];
    const merged = [head];
    let j = i + 1;
    while (j < src.length && isSameStop(head, src[j])) {
      merged.push(src[j]);
      j++;
    }
    const last = merged[merged.length - 1];
    // Group status: COMPLETED only if every order is; else surface a
    // failed/skipped one if present; otherwise the head's status.
    const FAILED = /FAIL|SKIP|ERROR/;
    let status = head.status;
    if (merged.every((s) => s.status === 'COMPLETED')) status = 'COMPLETED';
    else {
      const failed = merged.find((s) => FAILED.test(String(s.status)));
      if (failed) status = failed.status;
    }
    groups.push({
      ...head,
      // Arrival = first step's; finish (= group departure) = last step's. Keep
      // both the optimizer-planned (original*) and driver-actual variants so the
      // Stops table's planned/actual cells render the whole-stop span.
      finishTime: last.finishTime,
      originalFinishTime: last.originalFinishTime,
      actualFinishTime: last.actualFinishTime,
      status,
      orderIds: merged.map((s) => s.orderId).filter((x) => x != null),
      orders: merged.map((s) => s.order).filter(Boolean),
      mergedSteps: merged,
      firstStep: head,
      lastStep: last,
    });
    i = j;
  }
  // stopNumber = the 1-based grouped position (matches the day-plan badge); also
  // expose it as `orderNumber` so existing table columns keyed on that field
  // show the grouped number instead of the raw 1..10 step order.
  return groups.map((g, idx) => ({ ...g, stopNumber: idx + 1, orderNumber: idx + 1 }));
}

export default mergeRouteSteps;
